import { Client, User } from '@mi-front/core';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Card,
  CardContent,
  CardHeader,
  IconButton,
  List,
  ListItemSecondaryAction,
} from '@mui/material';
import { useIntl } from 'react-intl';
import { useImpersonation } from '../hooks/useImpersonationStore';
import { ImpersonationUserListItem } from './ImpersonationUserListItem';

type RecentUser = (User & { client: Client }) & {
  lastUsed: number;
};

interface Props {
  onUserSelect: (user: User & { client: Client }) => void;
}

export function RecentUsersQuickLogin({ onUserSelect }: Props) {
  const { $t } = useIntl();
  // Get current user and recent users from Zustand
  const {
    impersonatedUser: currentUser,
    recentUsers: allRecentUsers,
    removeRecentUser,
  } = useImpersonation();

  // Filter out the current user from recent users
  const recentUsers = allRecentUsers.filter((user) => user.id !== currentUser?.id);

  // Function to handle user click
  const handleUserClick = (user: RecentUser) => {
    onUserSelect(user);
    // Note: addRecentUser is now handled automatically in setImpersonatedUser
  };

  // Function to handle user delete
  const handleUserDelete = (userId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    removeRecentUser(userId);
  };

  if (recentUsers.length === 0) {
    return null;
  }

  return (
    <Card sx={{ marginTop: 4 }}>
      <CardHeader
        title={$t({
          id: 'impersonation.recentUsersTitle',
          defaultMessage: 'Recently impersonated users',
        })}
        titleTypographyProps={{
          variant: 'h6',
        }}
      />
      <CardContent>
        <List>
          {recentUsers.map((user) => (
            <ImpersonationUserListItem
              key={user.id}
              user={user}
              button
              onClick={() => handleUserClick(user)}
            >
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  aria-label={$t({
                    id: 'impersonation.deleteAriaLabel',
                    defaultMessage: 'Delete',
                  })}
                  onClick={(e) => handleUserDelete(user.id, e)}
                  size="small"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ImpersonationUserListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
}
